import { DateTime } from 'luxon';

// A local date range (YYYY-MM-DD, inclusive) the analytics endpoints accept.
export interface DateRange {
  from: string;
  to: string;
}

export type RangePreset = 'TODAY' | 'YESTERDAY' | 'THIS_WEEK' | 'LAST_7' | 'THIS_MONTH';

/** Ordered presets; labels come from `t.reports.rangePresets` (PeriodBar). */
export const RANGE_PRESETS: RangePreset[] = [
  'TODAY',
  'YESTERDAY',
  'THIS_WEEK',
  'LAST_7',
  'THIS_MONTH',
];

const fmt = (d: DateTime) => d.toFormat('yyyy-MM-dd');

// Resolve a preset to a local date range in the restaurant's timezone.
export function resolveRange(preset: RangePreset, tz: string): DateRange {
  const now = DateTime.now().setZone(tz);
  switch (preset) {
    case 'TODAY':
      return { from: fmt(now), to: fmt(now) };
    case 'YESTERDAY': {
      const y = now.minus({ days: 1 });
      return { from: fmt(y), to: fmt(y) };
    }
    case 'THIS_WEEK':
      return { from: fmt(now.startOf('week')), to: fmt(now) };
    case 'LAST_7':
      return { from: fmt(now.minus({ days: 6 })), to: fmt(now) };
    case 'THIS_MONTH':
      return { from: fmt(now.startOf('month')), to: fmt(now) };
  }
}

/**
 * Compact label for a range: "3 Aug" for a single day, "1 – 7 Aug" otherwise.
 * Used for the "vs …" caption on the comparison cards.
 */
export function formatRangeLabel(range: DateRange, locale: string): string {
  const from = DateTime.fromISO(range.from).setLocale(locale);
  const to = DateTime.fromISO(range.to).setLocale(locale);
  if (range.from === range.to) return from.toFormat('d LLL');
  const sameMonth = from.hasSame(to, 'month') && from.hasSame(to, 'year');
  return `${from.toFormat(sameMonth ? 'd' : 'd LLL')} – ${to.toFormat('d LLL')}`;
}

/** Today as a local YYYY-MM-DD in the restaurant timezone (for the day-end picker). */
export function todayLocal(tz: string): string {
  return fmt(DateTime.now().setZone(tz));
}
